import type { CategoryData, CategoryFilterState, CategoryPage } from '@/types';
import { getData } from '@/utils/getData';
import { getAuthUserToken } from '@/utils/searchAnalytics';
import { useCallback, useMemo, useRef, useState } from 'react';
import { buildFilterQuery } from './buildFilterQuery';

// Mirrors SearchConstants.FacetNameMap — maps GroupName to the Algolia index attribute name.
const FACET_NAME_MAP: Record<string, string> = {
  Author: 'author',
  Format: 'format',
  ProductType: 'productType',
  BookType: 'bookType',
  Brand: 'brand',
  Colour: 'colour',
  AgeRange: 'ageRange',
  ProductSize: 'productSize',
  PackSize: 'packSize',
  BindType: 'bindType',
  NibSize: 'nibSize',
  PriceRange: 'priceRange',
  PublicationDate: 'publicationDateRange',
};

export const toAlgoliaFacetName = (groupName: string): string =>
  FACET_NAME_MAP[groupName] ?? groupName.charAt(0).toLowerCase() + groupName.slice(1);

const parseFilterFromUrl = (
  initial: CategoryData,
  defaultSort: string,
): CategoryFilterState => {
  const params = new URLSearchParams(window.location.search);
  const facetGroupNames = new Set(
    initial.facetGroups.map(g => g.groupName),
  );

  const selectedFacets: Record<string, string[]> = {};
  for (const name of facetGroupNames) {
    const value = params.get(name);
    if (value) {
      selectedFacets[name] = value.split(';').filter(Boolean);
    }
  }

  return {
    sort: params.get('sort') ?? defaultSort,
    inStock: params.get('instock') === 'true',
    selectedFacets,
  };
};

const pushFilterToUrl = (filter: CategoryFilterState, page?: number) => {
  const params = new URLSearchParams();

  if (page && page > 1) {
    params.set('page', String(page));
  }
  if (filter.sort) {
    params.set('sort', filter.sort);
  }
  if (filter.inStock) {
    params.set('instock', 'true');
  }
  for (const [group, keys] of Object.entries(filter.selectedFacets)) {
    if (keys.length > 0) {
      params.set(group, keys.join(';'));
    }
  }

  const qs = params.toString();
  const url = window.location.pathname + (qs ? `?${qs}` : '');
  window.history.replaceState(null, '', url);
};

const buildNextPageUrl = (baseUrl: string, filter: CategoryFilterState, nextPage: number): string => {
  const params = new URLSearchParams();
  params.set('page', String(nextPage));
  if (filter.sort) params.set('sort', filter.sort);
  if (filter.inStock) params.set('instock', 'true');
  for (const [group, keys] of Object.entries(filter.selectedFacets)) {
    if (keys.length > 0) params.set(group, keys.join(';'));
  }
  return `${baseUrl}?${params.toString()}`;
};

export const useCategoryListing = (initial: CategoryData) => {
  const initialSort =
    initial.sorting.find(s => s.selected)?.value ?? 'popularity';
  const initialFilter = useRef(
    parseFilterFromUrl(initial, initialSort),
  ).current;
  const [filter, setFilter] = useState<CategoryFilterState>(initialFilter);
  const [pages, setPages] = useState<CategoryPage[]>([initial]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Bumped in the same state update that replaces or extends `pages`, so it is
  // a signature for the data actually being reported. Deriving that signature
  // from `filter` instead would change it while `pages` still held the old
  // products: the pre-filter set would be re-reported under the new filter, and
  // the filtered set - arriving with an unchanged filter - would never fire.
  const [resultVersion, setResultVersion] = useState(0);

  const fetchPage = useCallback(
    async (page: number, currentFilter: CategoryFilterState): Promise<CategoryPage> => {
      const query = buildFilterQuery(page, currentFilter);
      return getData(initial.filterEndpoint, query)() as Promise<CategoryPage>;
    },
    [initial.filterEndpoint],
  );

  // Fetch page 1 with a new filter, replace all pages, and update the URL.
  const applyFilter = useCallback(
    (newFilter: CategoryFilterState) => {
      setFilter(newFilter);
      pushFilterToUrl(newFilter);
      setIsLoading(true);
      setHasError(false);
      fetchPage(1, newFilter).then(result => {
        setPages([result]);
        setResultVersion(v => v + 1);
        setIsLoading(false);
      });
    },
    [fetchPage],
  );

  const firstPage = pages[0] ?? initial;
  const lastPage = pages[pages.length - 1];
  const products = useMemo(() => pages.flatMap(p => p.products), [pages]);
  const totalPages = firstPage.totalPages ?? Math.ceil(firstPage.totalCount / firstPage.pageSize);
  const currentMaxPage = lastPage?.page ?? initial.page;
  const hasMore = !hasError && currentMaxPage < totalPages;
  const nextPage = hasMore ? currentMaxPage + 1 : null;
  const nextPageUrl = nextPage
    ? buildNextPageUrl(initial.categoryBaseUrl, filter, nextPage)
    : null;

  const loadMore = useCallback(() => {
    if (!nextPage || isLoadingMore) return;
    setIsLoadingMore(true);
    fetchPage(nextPage, filter)
      .then(result => {
        if (!result.products || result.products.length === 0) {
          setHasError(true);
          return;
        }
        setPages(prev => {
          const updated = [...prev, result];
          pushFilterToUrl(filter, result.page);
          return updated;
        });
        setResultVersion(v => v + 1);
      })
      .catch(() => {
        // Request failed (404, network error, etc.) — stop further loading.
        // applyFilter resets pages, so the button reappears on filter change.
        setHasError(true);
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [nextPage, isLoadingMore, filter, fetchPage]);

  // `groupName` is the facet param key sent to the server: the binder keys
  // facet groups by GroupName (FacetGroups.IsBelong), so selectedFacets — which
  // becomes the query-param name via buildFilterQuery — must be keyed by it.
  const toggleFacet = (groupName: string, key: string) => {
    // Algolia insight: "Search Filter Clicked" — only on selection, not deselection
    // Migrated from _AlgoliaInsightEvents.cshtml [data-algolia-facet] click listener
    const current = filter.selectedFacets[groupName] ?? [];
    const isSelecting = !current.includes(key);
    if (isSelecting) {
      window.dataLayer?.push({
        event: 'Search Filter Clicked',
        authUserToken: getAuthUserToken(),
        filters: `${toAlgoliaFacetName(groupName)}:${key}`,
      });
    }

    const prevKeys = filter.selectedFacets[groupName] ?? [];
    const next = prevKeys.includes(key)
      ? prevKeys.filter(k => k !== key)
      : [...prevKeys, key];
    applyFilter({
      ...filter,
      selectedFacets: { ...filter.selectedFacets, [groupName]: next },
    });
  };

  const hasActiveFilters =
    filter.inStock ||
    Object.values(filter.selectedFacets).some(keys => keys.length > 0);

  // Only the newest page is reported to GA4 - `products` is cumulative, so
  // re-sending it on every loadMore would re-count impressions. The offset is
  // derived from the page number rather than from how many products happen to
  // be loaded, so a deep-linked `?page=3` reports 48..71 rather than 0..23.
  const latestPageProducts = lastPage?.products ?? [];
  const latestPageOffset = Math.max(0, (currentMaxPage - 1) * firstPage.pageSize);

  return {
    products,
    facetGroups: firstPage.facetGroups,
    sorting: firstPage.sorting,
    totalCount: firstPage.totalCount,
    loadedCount: products.length,
    algoliaQueryId: firstPage.algoliaQueryId,
    query: initial.query,
    listName: initial.listName,
    contentId: initial.contentId,
    latestPageProducts,
    latestPageOffset,
    resultVersion,
    noResultsLabel: initial.noResultsLabel,
    noResultsImage: initial.noResultsImage,
    isLoading,
    isLoadingMore,
    hasMore,
    nextPageUrl,
    filter,
    hasActiveFilters,
    clearAll: () => applyFilter({ ...filter, inStock: false, selectedFacets: {} }),
    loadMore,
    setSort: (sort: string) => applyFilter({ ...filter, sort }),
    setInStock: (inStock: boolean) => applyFilter({ ...filter, inStock }),
    toggleFacet,
    showMoreLikeThis: initial.showMoreLikeThis ?? false,
  };
};
